import { CommonHelper } from "../../../../../../../react-core/Helpers/CommonHelper";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { ClientAccount } from "../../../../../../../Wrap/Models/Domains/Account/SlAccounts/ClientAccount";
import { InvestorType } from "../../../../../../../Wrap/Models/Domains/Account/SlAccounts/InvestorType";
import { InvestorType as WrapperSelectionInvestorType } from "../../../../../../../Wrap/Constants/Enums/Domain/InvestorType";
import { SubAccount } from "../../../../../../../Wrap/Models/Domains/Account/SlAccounts/SubAccount";
import { QuoteAndApplyBaseModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/QuoteAndApplyBaseModel";
import { SubAccountActionIndicators } from "../../../../../../../Wrap/Models/Models/Shared/SubAccountActionIndicators";
import { SubAccountTypeHelper } from "../../../Common/Helpers/SubAccountTypeHelper";

export const isSelectedAndExisted = (
    predicate: (type: SubAccountType) => boolean,
    selectedProducts: SubAccountActionIndicators[],
    currentProducts: SubAccount[]
): { existed: boolean; selected: boolean } => {
    const selected = (selectedProducts || []).some((x) => predicate(x.SubAccountType));
    const existed = (currentProducts || []).some((x) => predicate(x.SubAccountType));
    return { existed: existed, selected: selected };
};

const personalPortfolioMsg = (
    isCreateNew: boolean,
    isJointAccount: boolean,
    selectedProducts: SubAccountActionIndicators[] = [],
    currentProducts: SubAccount[] = []
): { msg: string[]; disableCashAccount: boolean } => {
    const msg: string[] = [];
    let disableCashAccount = false;
    /** Personal Portfolio */
    const { selected, existed } = isSelectedAndExisted(
        (x) => SubAccountTypeHelper.isPP(x),
        selectedProducts,
        currentProducts
    );
    // new account , joint , selected PP
    if (isCreateNew && isJointAccount && selected) {
        disableCashAccount = true;
        msg.push(
            "As you have selected to set up a new Joint PP product, once this business has been submitted a new WP header account will be created."
        );
    }
    // no exist PP, selected PP
    if (!existed && selected) {
        msg.push(
            "This process allows this client to set up either 1 joint or 1 individual PP sub account. If you wish to set up one of each type you will need to complete the process for the Joint and Individual separately."
        );
    }
    // existed PP, selected PP
    if (existed && selected) {
        msg.push(
            "The selected account already contains an existing PP sub-account. This process allows you to set up either 1 joint or 1 individual PP sub account. If you want to add further contributions to the existing PP sub account, please use the Single Payment/Transfer or Regular Payments process."
        );
    }
    return { msg, disableCashAccount };
};

const offshoreBondMsg = (
    isCreateNew: boolean,
    isJointAccount: boolean,
    selectedProducts: SubAccountActionIndicators[] = [],
    currentProducts: SubAccount[] = []
): { msg: string[]; disableCashAccount: boolean } => {
    const msg: string[] = [];
    let disableCashAccount = false;
    /** Onshore Bond */
    const { selected, existed } = isSelectedAndExisted(
        (x) => x == SubAccountType.OffshoreBond,
        selectedProducts,
        currentProducts
    );
    //not new account, not existed, selected
    if (!isCreateNew && !existed && selected) {
        msg.push(
            `This process allows this client to set up either 1 joint or 1 individual Offshore Bond sub account. If you wish to set up one of each type you will need to complete the process for the Joint and Individual separately.`
        );
    }
    //not new account, not existed, selected , individual
    if (!isCreateNew && !existed && selected && !isJointAccount) {
        msg.push(
            "You have selected to open a new Offshore Bond product, please do so through the New Business Existing Client process."
        );
    }
    // new account, selected, joint
    if (isCreateNew && selected && isJointAccount) {
        disableCashAccount = true;
        msg.push(
            "As you have selected to set up a new Joint Offshore Bond product, once this business has been submitted a new WP header account will be created."
        );
    }
    return { msg, disableCashAccount };
};

const onshoreBondMsg = (
    isCreateNew: boolean,
    isJointAccount: boolean,
    selectedProducts: SubAccountActionIndicators[] = [],
    currentProducts: SubAccount[] = []
): { msg: string[]; disableCashAccount: boolean } => {
    const msg: string[] = [];
    let disableCashAccount = false;
    /** Onshore Bond */
    const { selected, existed } = isSelectedAndExisted(
        (x) => x == SubAccountType.OnshoreBond,
        selectedProducts,
        currentProducts
    );
    //no existed, selected
    if (!existed && selected) {
        msg.push(
            `This process allows this client to set up either 1 joint or 1 individual Onshore Bond sub account. If you wish to set up one of each type you will need to complete the process for the Joint and Individual separately.`
        );
    }
    //not new account, not existed, selected , individual
    if (!isCreateNew && !existed && selected && !isJointAccount) {
        msg.push(
            "You have selected to open a new Onshore Bond product, please do so through the New Business Existing Client process."
        );
    }
    // new account, selected, joint
    if (isCreateNew && selected && isJointAccount) {
        disableCashAccount = true;
        msg.push(
            "As you have selected to set up a new Joint Onshore Bond product, once this business has been submitted a new WP header account will be created."
        );
    }
    return { msg, disableCashAccount };
};

const isaMsg = (isCreateNew: boolean, currentProducts: SubAccount[] = []): { msg: string[] } => {
    const msg: string[] = [];
    const product = currentProducts.find((ele) => SubAccountTypeHelper.isIsa(ele.SubAccountType));
    if (!isCreateNew && product) {
        msg.push(
            `The selected account already contains an existing ISA sub-account. The Account Number is ${product?.ClAccountId}. Please use the Single Payment/Transfer or Regular Payments process to add a contribution to this account.`
        );
    }
    return { msg };
};

const sippMsg = (isCreateNew: boolean, currentProducts: SubAccount[] = []): { msg: string[] } => {
    const msg: string[] = [];
    const product = currentProducts.find((ele) => SubAccountTypeHelper.isOnPlatformPension(ele.SubAccountType));
    if (!isCreateNew && product) {
        msg.push(
            `As your client already has an existing SIPP, you will only be able to add transfers in for one or more post-retirement SIPP accounts. Please use the Single Payment/Transfer or Regular Payments process if you want to add more contributions to an existing SIPP.`
        );
    }
    return { msg };
};

export const getWarningMessage = (
    isCreateNew: boolean,
    isJointAccount: boolean,
    selectedProducts: SubAccountActionIndicators[] = [],
    currentProducts: SubAccount[] = [],
    isNbec: boolean
): string[] => {
    let msg: string[] = [];
    if (isCreateNew) {currentProducts = [];}
    msg = msg.concat(isaMsg(isCreateNew, currentProducts).msg);
    msg = msg.concat(personalPortfolioMsg(isCreateNew, isJointAccount, selectedProducts, currentProducts).msg);
    msg = msg.concat(onshoreBondMsg(isCreateNew, isJointAccount, selectedProducts, currentProducts).msg);
    msg = msg.concat(offshoreBondMsg(isCreateNew, isJointAccount, selectedProducts, currentProducts).msg);
    msg = msg.concat(sippMsg(isCreateNew, currentProducts).msg)
    if (isCreateNew && isNbec && isJointAccount == false)
        {msg = msg.concat(
            "As you have selected to set up a new Individual Account, once this business has been submitted a new WP header account will be created."
        );}
    return msg;
};

export const disableCashAccount = (
    isCreateNew: boolean,
    isJointAccount: boolean,
    selectedProducts: SubAccountActionIndicators[] = [],
    currentProducts: SubAccount[] = [],
    isNbec: boolean
): boolean => {
    let isDisableCashAccount = false;
    if (isCreateNew) {currentProducts = [];}
    isDisableCashAccount =
        personalPortfolioMsg(isCreateNew, isJointAccount, selectedProducts, currentProducts).disableCashAccount ||
        onshoreBondMsg(isCreateNew, isJointAccount, selectedProducts, currentProducts).disableCashAccount ||
        offshoreBondMsg(isCreateNew, isJointAccount, selectedProducts, currentProducts).disableCashAccount ||
        (isCreateNew && isNbec && isJointAccount == false);

    return isDisableCashAccount;
};

export const isJointOrTrustAccount = (investorType: InvestorType): boolean => {
    return [InvestorType.Joint, InvestorType.JointIndividual, InvestorType.Trust].includes(investorType);
};

export const isCreatingNewAccount = (
    hierarchyId: string,
    data: QuoteAndApplyBaseModel,
    allAccounts: ClientAccount[]
): boolean => {
    if (CommonHelper.isNullOrEmpty(hierarchyId)) {
        return true;
    }
    if (data?.IsJointAccount === true && CustomerHasIndividualButNoJointAccount(allAccounts)) {
        return true;
    }
    if (data?.IsJointAccount === false && CustomerHasJointButNoIndividualAccount(allAccounts)) {
        return true;
    }
    return false;
};

const accountIsJoint = (clientAccount: ClientAccount) => {
    return [InvestorType.Joint, InvestorType.JointIndividual].includes(clientAccount?.InvestorType);
};

const accountIsIndividual = (clientAccount: ClientAccount) => {
    return clientAccount?.InvestorType == InvestorType.Individual;
};

const CustomerHasJointButNoIndividualAccount = (accountRoles: ClientAccount[]) => {
    return accountRoles?.length > 0 && accountRoles?.every((x) => accountIsJoint(x));
};

const CustomerHasIndividualButNoJointAccount = (accountRoles: ClientAccount[]) => {
    return accountRoles?.length > 0 && accountRoles?.every((x) => accountIsIndividual(x));
};
